import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { ContentFilterKeyName, ContentFiltersKeys } from '../../../ts/models/ContentFilters';
import {
  mixedContentFilterApplied,
  setContentFilterNoRequestContent
} from '../../actions/spaces/filters/FilterActions';
import { useIsMounted, usePrevious } from '../../hooks';
import { CompositeSessionInfoSelectors } from '../../selectors/SessionInfoSelectors';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';

export interface FilterUpdaterOwnProps {
  readonly routeLocationSearch: string;
  readonly routeLocationPath: string;
  readonly resetStore?: boolean;
}

export interface FilterUpdaterStateProps {
  readonly isLimitedUserVisibilityEnforced: boolean;
}

export interface FilterUpdaterDispatchProps {
  readonly mixedContentFilterApplied: (filterType: ContentFilterKeyName, value: string) => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
}

export type FilterUpdaterProps = FilterUpdaterOwnProps & FilterUpdaterStateProps & FilterUpdaterDispatchProps;

/**
 * Component that syncs the filters from the URL query params to the Redux store before rendering its children.
 */
export const FilterUpdater: React.FunctionComponent<FilterUpdaterProps> = props => {
  const isMounted = useIsMounted();
  const prevRouteLocationPath = usePrevious(props.routeLocationPath);
  const curQueryValues = queryString.parse(props.routeLocationSearch);
  const prevQueryValues = usePrevious(curQueryValues) || [];

  const filterKeys = props.isLimitedUserVisibilityEnforced
    ? _.without(_.keys(ContentFiltersKeys), ContentFiltersKeys.owner)
    : _.keys(ContentFiltersKeys);

  // Before the initial render, initialize the content filters in the store
  // TFSID:983648.  Handle case when updating this component with a new projectId
  if (!isMounted || props.resetStore) {
    filterKeys.forEach((filterKey: ContentFilterKeyName) => {
      props.setContentFilterNoRequestContent(filterKey, getFilterValue(curQueryValues[filterKey]));
    });
  }

  // Whenever the query string changes, update the content filters that have changed
  React.useEffect(() => {
    if (isMounted && prevRouteLocationPath === props.routeLocationPath) {
      filterKeys.forEach((filterKey: ContentFilterKeyName) => {
        if (prevQueryValues[filterKey] !== curQueryValues[filterKey]) {
          props.mixedContentFilterApplied(filterKey, getFilterValue(curQueryValues[filterKey]));
        }
      });
    }
  }, [ props.routeLocationSearch ]);

  return <>{props.children}</>;
};

function getFilterValue(raw: any): string {
  if (raw && !_.isString(raw)) {
    return String(raw);
  }
  return raw;
}

const mapStateToProps: MapStateToProps<FilterUpdaterStateProps> = state => ({
  isLimitedUserVisibilityEnforced: CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced(state),
});

const mapDispatchToProps: MapDispatchToProps<FilterUpdaterDispatchProps> = dispatch => bindActionCreators({
  mixedContentFilterApplied,
  setContentFilterNoRequestContent,
}, dispatch);

export const ConnectedFilterUpdater = connect(mapStateToProps, mapDispatchToProps)(FilterUpdater);

/**
 * Creates a higher order component that wraps the given component with the FilterUpdater component.
 */
export function withFilterUpdater<P>(Component: React.ComponentType<P>) {
  return (props: P & FilterUpdaterProps) => (
    <ConnectedFilterUpdater {...props}><Component {...props}/></ConnectedFilterUpdater>
  );
}
